import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';

export interface UnitErrorAffectedUnit {
  serial: string;
  count: number;
  last_seen: string;
}

export interface UnitErrorTimelinePoint {
  date: string;
  count: number;
}

export interface UnitErrorBreakdownItem {
  device_type?: string;
  software_release?: string;
  label?: string;
  count?: number;
}

export interface UnitErrorDetail {
  id: string;
  message: string;
  count: number;
  unique_units: number;
  affected_units: UnitErrorAffectedUnit[];
  timeline: UnitErrorTimelinePoint[];
  breakdowns: {
    hardware_types: UnitErrorBreakdownItem[];
    software_releases: UnitErrorBreakdownItem[];
  };
}

/**
 * Fetches the detail payload for a single unit-error.
 *
 * The id is the base64url encoding of the message (URL-safe), which the backend
 * decodes server-side, so the request needs no extra query params. We hit it
 * through dataProvider.get rather than the standard react-admin getOne,
 * mirroring the custom endpoint hooks in this folder (see useUnitKeyManagement).
 * The response wraps the record in a Laravel `data` object, which we unwrap.
 */
export function useUnitErrorDetail(id: string): UseQueryResult<UnitErrorDetail> {
  const dataProvider = useDataProvider();

  return useQuery<UnitErrorDetail>({
    queryKey: ['unit-error-detail', id],
    queryFn: async () => {
      // dataProvider.get prepends the base API URL (v3/api).
      const response = await dataProvider.get(`dashboard/unit-errors/${id}`);
      return response.data as UnitErrorDetail;
    },
    enabled: !!id,
  });
}
